import { Localized, useLocalization } from '@fluent/react'
import TableContainer from '@mui/material/TableContainer'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import { Link, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { useEffect, useState } from 'react'
import { Device, loadDevices, selectAllDevices, selectDevices } from './deviceSlice'
import { ButtonGroup } from '@mui/material'
import { loadDeviceTypes, selectDeviceTypes } from './deviceTypesSlice'
import { Skeleton } from '@mui/material';
import { FlexDiv } from '../../app/Utils'
import { selectModule } from "../modules/moduleSlice";
import { selectSettings } from '../settings/settingsSlice'
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { enUS, plPL } from "@mui/x-data-grid/locales"
import { AddFabButton } from "../../app/components/AddFabButton";
import { CreateDeviceDialog } from './dialogs/DeviceCreateEditDialog'
import { StructureName } from './Structure'
import { QuickSearchToolbar } from '../../app/QuickSearchToolbar'
import { entityRoutes } from '../../app/components/EntityBreadcrumbs'

export const Devices = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings);
    const { state: devicesState } = useAppSelector(selectDevices)
    const { state: deviceTypesState } = useAppSelector(selectDeviceTypes)
    const currentModule =
        useAppSelector(state => selectModule(state).currentModule)

    const allDevices = useAppSelector(selectAllDevices)

    const [openCreate, setOpenCreate] = useState(false);
    const handleOpen = () => setOpenCreate(true);
    const handleCancel = () => setOpenCreate(false);


    useEffect(() => {
        if (devicesState.type === "empty" && currentModule !== null) {
            dispatch(loadDevices(currentModule.sysModuleId))
        }
        if (deviceTypesState.type === "empty") {
            dispatch(loadDeviceTypes())
        }

    }, [dispatch, devicesState, deviceTypesState, currentModule])

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const handleSuccess = (device: Device): void => {
        GoToDeviceForm(device.id)

    }

    const GoToDeviceForm = (id) => {
        navigate(entityRoutes.device(id))
    };

    const columns: GridColDef[] = [
        {
            field: 'name',
            headerName: l10n.getString("devices-name", null, "Nazwa"),
            flex: 1
        },
        {
            field: 'structureId',
            headerName: l10n.getString("structure", null, "_structure"),
            flex: 1,
            minWidth: 200,

            renderCell: (params) => {
                return (
                    <StructureName structureId={params.row.structureId} />
                )
            }
        },
        {
            field: 'supplierName',
            headerName: l10n.getString("devices-producer", null, "_Producent"),
            width: 300

        },
        {
            field: 'serialNo',
            headerName: l10n.getString("devices-serial-number", null, "Nr fabryczny"),
            minWidth: 200,
            valueGetter: (_, row) => {
                const unknownNo = row.unknownNo;
                const serialNo = row.serialNo;
                return unknownNo ? l10n.getString("device-unknown-no", null, "_Nieznany numer fabryczny") : serialNo || "—";
            }
        },
        {
            field: 'productionYear',
            headerName: l10n.getString("devices-production-year", null, "Rok produkcji"),
            width: 150
        },
        {
            field: 'actions', type: 'actions', width: 200,
            renderCell: (params) => {
                return (
                    <ButtonGroup size="small">
                        <Button
                            style={{ width: 75 }}
                            component={Link}
                            to={entityRoutes.device(params.row.id)}
                            color="secondary"
                            variant="outlined" >
                            <Localized id="edit">
                                <span>Edytuj</span>
                            </Localized>
                        </Button>
                        <Button component={Link}
                            state={{ deviceId: params.row.id }}
                            to={{
                                pathname: "/process/new",
                            }}
                            color="secondary"
                            variant="outlined">
                            <Localized id="devices-run-validation">
                                <span>Waliduj</span>
                            </Localized>
                        </Button>
                    </ButtonGroup>
                )
            }
        }
    ]

    switch (devicesState.type) {
        case "empty":
        case "loading":
        case "error":
            return <Skeleton animation="wave" variant="rectangular" height="64vh" ></Skeleton>;
        case "loaded":
            return (
                <>
                    <TableContainer component={Paper}>
                        <DataGrid
                            localeText={getCustomLocaleText()}
                            style={{ minHeight: 100, height: 'calc(100vh - 200px)' }}
                            rows={allDevices}
                            columns={columns}
                            slots={{ toolbar: QuickSearchToolbar }}
                            onRowDoubleClick={({ row }) => GoToDeviceForm(row.id)}

                            initialState={{
                                sorting: { sortModel: [{ field: 'name', sort: 'asc' }] }
                            }}
                        />
                        <FlexDiv>
                            <AddFabButton
                                onClick={handleOpen}
                            />
                        </FlexDiv>
                    </TableContainer>
                    {openCreate && (
                        <CreateDeviceDialog
                            onCancel={handleCancel}
                            onSuccess={handleSuccess}
                        />
                    )}
                </>
            );
    }
}
